import { useCallback, useEffect, useState } from "react";
import { ConstellationAPI } from "../infrastructure/api/Constellation.api";
import { BoardState, ConstellationScene } from "../domain/types";

export type ScenesStatus = "idle" | "loading" | "ready" | "error";

export interface UseScenesResult {
    scenes: ConstellationScene[];
    status: ScenesStatus;
    reload: () => Promise<void>;
    createFromBoard: (
        boardId: string,
        name: string,
        description?: string,
    ) => Promise<ConstellationScene>;
    createFromState: (
        state: BoardState,
        name: string,
        opts?: { description?: string; isTemplate?: boolean },
    ) => Promise<ConstellationScene>;
    rename: (sceneId: string, name: string) => Promise<void>;
    remove: (sceneId: string) => Promise<void>;
}

/** Lists and mutates the therapist's saved scenes. Optionally filters by
 *  patient / template. Exposes explicit loading / ready / error states. */
export function useScenes(filter?: {
    patientId?: string;
    isTemplate?: boolean;
}): UseScenesResult {
    const [scenes, setScenes] = useState<ConstellationScene[]>([]);
    const [status, setStatus] = useState<ScenesStatus>("idle");

    const patientId = filter?.patientId;
    const isTemplate = filter?.isTemplate;

    const reload = useCallback(async () => {
        setStatus("loading");
        try {
            const { data } = await ConstellationAPI.listScenes({
                patient_id: patientId,
                is_template: isTemplate,
            });
            setScenes(data.items);
            setStatus("ready");
        } catch {
            setStatus("error");
        }
    }, [patientId, isTemplate]);

    useEffect(() => {
        void reload();
    }, [reload]);

    const createFromBoard = useCallback(
        async (boardId: string, name: string, description?: string) => {
            const { data } = await ConstellationAPI.createScene({
                name,
                description,
                board_id: boardId,
                patient_id: patientId,
            });
            await reload();
            return data;
        },
        [patientId, reload],
    );

    const createFromState = useCallback(
        async (
            state: BoardState,
            name: string,
            opts?: { description?: string; isTemplate?: boolean },
        ) => {
            const { data } = await ConstellationAPI.createScene({
                name,
                description: opts?.description,
                state,
                is_template: opts?.isTemplate,
                patient_id: opts?.isTemplate ? undefined : patientId,
            });
            await reload();
            return data;
        },
        [patientId, reload],
    );

    const rename = useCallback(
        async (sceneId: string, name: string) => {
            await ConstellationAPI.updateScene(sceneId, { name });
            await reload();
        },
        [reload],
    );

    const remove = useCallback(
        async (sceneId: string) => {
            await ConstellationAPI.deleteScene(sceneId);
            await reload();
        },
        [reload],
    );

    return { scenes, status, reload, createFromBoard, createFromState, rename, remove };
}
